import React from 'react';
import { DetailsInfoProps } from './types';
import { useParams } from 'react-router-dom';
import { MissionIdParams } from '@src/core/entities';
import { useGetOrderMissionQuery } from '@src/features/order/redux';
import { OrderMissionEntity, useGetUserDataQuery } from '@src/core/features';
import { ArcCellInfo, ArcSkeleton, ArcStack, ArcText } from '@src/core/ui';
import { EMPTY_STRING } from '@src/core/config';
import { arcFormatCoordinateDisplay, arcSpatium, arcVelocitas } from '@src/core/extensions';
import { formatDeliveryMode } from '@src/features/order/utils';

const getOrderWP = (data: OrderMissionEntity) =>
  data.waypoints?.find((w) => Boolean(w.orderId));

const getTakeOff = (data: OrderMissionEntity) =>
  data.waypoints?.[0];


const DetailsInfo: React.FC<DetailsInfoProps> = (props) => {
  const { missionId } = useParams<MissionIdParams>();
  const { missionId: propsMissionId } = props;
  const effectiveMissionId = propsMissionId || missionId;

  const { data, isLoading } = useGetOrderMissionQuery(effectiveMissionId, { skip: !effectiveMissionId });

  const { data: userData } = useGetUserDataQuery();

  if (isLoading || !data) {
    return <ArcSkeleton height={250} />;
  }

  const orderWp = getOrderWP(data);

  const takeOffWp = getTakeOff(data);


  const [order] = data?.orders ?? [];
  const takeoff = order?.takeoffLocation;
  const destination = order?.destinationLocation;

  return (
    <ArcStack p={2} borderRadius={1} spacing={2} bgcolor='background.paper'>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Order' width='50%'>
          #{order?.id}
        </ArcCellInfo>
        <ArcCellInfo label='HUB' width='50%'>
          {userData?.hub?.name || takeoff?.address || EMPTY_STRING}
        </ArcCellInfo>
      </ArcStack>

      <ArcCellInfo label='Takeoff Location' width='100%'>
        {takeoff?.address || EMPTY_STRING}
      </ArcCellInfo>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Takeoff Latitude' width='50%'>
          {arcFormatCoordinateDisplay(takeoff?.latitude, EMPTY_STRING)}
        </ArcCellInfo>
        <ArcCellInfo label='Takeoff Longitude' width='50%'>
          {arcFormatCoordinateDisplay(takeoff?.longitude, EMPTY_STRING)}
        </ArcCellInfo>
      </ArcStack>

      <ArcCellInfo label='Destination Location' width='100%'>
        {destination?.address || EMPTY_STRING}
      </ArcCellInfo>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Destination Latitude' width='50%'>
          {arcFormatCoordinateDisplay(destination?.latitude, EMPTY_STRING)}
        </ArcCellInfo>
        <ArcCellInfo label='Destination Longitude' width='50%'>
          {arcFormatCoordinateDisplay(destination?.longitude, EMPTY_STRING)}
        </ArcCellInfo>
      </ArcStack>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Takeoff Altitude' width='50%'>
          {arcSpatium(Number(takeOffWp?.altitude), { fromSi: true })}
        </ArcCellInfo>
        <ArcCellInfo label='Destination Altitude' width='50%'> 
          {arcSpatium(Number(orderWp?.altitude), { fromSi: true })}
        </ArcCellInfo>
      </ArcStack>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Delivery Altitude' width='50%'>
          {arcSpatium(Number(orderWp?.deliveryAltitude), { fromSi: true })}
        </ArcCellInfo>
        <ArcCellInfo label='Cruise Speed' width='50%'> 
          {arcVelocitas(Number(orderWp?.speed), { fromSi: true })}
        </ArcCellInfo>
      </ArcStack>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Delivery Mode' width='50%'>
          {formatDeliveryMode(order?.deliveryMode)}
        </ArcCellInfo>
        <ArcCellInfo label='Drone Name' width='50%'> 
          {data?.drone?.name}
        </ArcCellInfo>
      </ArcStack>

      <ArcStack direction='row' spacing={1}>
        <ArcCellInfo label='Package Content' width='50%'>
          {order?.packageContent || EMPTY_STRING}
        </ArcCellInfo>
        <ArcCellInfo label='Client Name' width='50%'>
          {order?.clientName || EMPTY_STRING}
        </ArcCellInfo>
      </ArcStack>
    </ArcStack>
  );
};

export default DetailsInfo;
